import React, { useState, useRef, useEffect } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i);

const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, placeholder, disabled, name, isArabic }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: isOpen ? '2px solid #ff7a1a' : '1px solid #e5e7eb',
          borderRadius: '12px',
          background: disabled ? '#f9fafb' : 'white',
          color: value ? '#1f2937' : '#9ca3af',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '44px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#d1d5db';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#e5e7eb';
          }
        }}
      >
        <span>{displayText}</span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={isOpen ? '#ff7a1a' : '#6b7280'} 
          strokeWidth="2"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            maxHeight: '220px', // Show approximately 5 options (44px per option)
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            return (
              <div
                key={option.value}
                onClick={() => {
                  onChange({ target: { name, value: option.value } });
                  setIsOpen(false);
                }}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: isSelected ? '#fff4e6' : 'transparent',
                  color: isSelected ? '#ff7a1a' : '#1f2937',
                  fontWeight: isSelected ? '600' : '400',
                  fontSize: '15px',
                  transition: 'all 0.15s',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Step3Experience({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const [experienceNone, setExperienceNone] = useState(wizardData.experience_none || false);
  const [experiences, setExperiences] = useState(wizardData.experiences || [
    { job_title: '', organization: '', from_month: '', from_year: '', to_month: '', to_year: '', present: false }
  ]);

  const handleToggleNoExperience = (e) => {
    const checked = e.target.checked;
    setExperienceNone(checked);
    if (checked) {
      setExperiences([{ job_title: '', organization: '', from_month: '', from_year: '', to_month: '', to_year: '', present: false }]);
    }
  };

  const handleAddExperience = () => {
    setExperiences([...experiences, { job_title: '', organization: '', from_month: '', from_year: '', to_month: '', to_year: '', present: false }]);
  };

  const handleRemoveExperience = (index) => {
    if (experiences.length > 1) {
      setExperiences(experiences.filter((_, i) => i !== index));
    }
  };

  const handleExperienceChange = (index, field, value) => {
    setExperiences(experiences.map((exp, i) => 
      i === index ? { ...exp, [field]: value } : exp
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const experienceData = {
      experience_none: experienceNone,
      experiences: experienceNone ? [] : experiences
    };

    await onSave(experienceData);
    onNext(experienceData);
  };

  // Prepare month options for dropdown
  const monthOptions = MONTHS.map((month, idx) => ({
    value: month,
    label: isArabic ? MONTHS_AR[idx] : month
  }));

  // Prepare year options for dropdown
  const yearOptions = YEARS.map(year => ({
    value: year.toString(),
    label: year.toString()
  }));

  return (
    <div>
      <h2 className="step-title">{t.experience || 'Experience'}</h2>
      <p className="step-subtitle">
        {t.experienceSubtitle || 'Share your teaching or professional experience. This helps students understand your background.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        {/* No Experience Checkbox */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={experienceNone}
              onChange={handleToggleNoExperience}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>{t.noExperience || 'I don\'t have any experience'}</span>
          </label>
        </div>

        {/* Experience Fields - Hidden if "no experience" checked */}
        {!experienceNone && (
          <>
            {experiences.map((exp, index) => (
              <div key={index} style={{ 
                padding: '20px', 
                border: '1px solid #e5e7eb', 
                borderRadius: '12px',
                marginBottom: '16px',
                background: '#f9fafb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {isArabic ? `الخبرة ${index + 1}` : `Experience ${index + 1}`}
                  </h3>
                  {experiences.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveExperience(index)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #dc2626',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#dc2626',
                        cursor: 'pointer'
                      }}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <div className="form-row" style={{ marginBottom: '16px' }}>
                  <div className="form-group">
                    <label>{t.jobTitle || 'Job Title'}</label>
                    <input
                      type="text"
                      value={exp.job_title}
                      onChange={(e) => handleExperienceChange(index, 'job_title', e.target.value)}
                      placeholder={isArabic ? 'مثال: معلم رياضيات، مدرس' : 'e.g., Math Teacher, Tutor'}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t.organization || 'Organization / Place'}</label>
                    <input
                      type="text"
                      value={exp.organization}
                      onChange={(e) => handleExperienceChange(index, 'organization', e.target.value)}
                      placeholder={isArabic ? 'مثال: اسم المدرسة، الجامعة' : 'e.g., School Name, University'}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={exp.present}
                      onChange={(e) => handleExperienceChange(index, 'present', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{t.currentlyWorkHere || 'I currently work here'}</span>
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t.from || 'From'}</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <CustomDropdown
                        name={`from_month_${index}`}
                        value={exp.from_month}
                        onChange={(e) => handleExperienceChange(index, 'from_month', e.target.value)}
                        options={[{ value: '', label: t.month || 'Month' }, ...monthOptions]}
                        placeholder={t.month || 'Month'}
                        isArabic={isArabic}
                      />
                      <CustomDropdown
                        name={`from_year_${index}`}
                        value={exp.from_year}
                        onChange={(e) => handleExperienceChange(index, 'from_year', e.target.value)}
                        options={[{ value: '', label: t.year || 'Year' }, ...yearOptions]}
                        placeholder={t.year || 'Year'}
                        isArabic={isArabic}
                      />
                    </div>
                  </div>
                  {!exp.present && (
                    <div className="form-group">
                      <label>{t.to || 'To'}</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <CustomDropdown
                          name={`to_month_${index}`}
                          value={exp.to_month}
                          onChange={(e) => handleExperienceChange(index, 'to_month', e.target.value)}
                          options={[{ value: '', label: t.month || 'Month' }, ...monthOptions]}
                          placeholder={t.month || 'Month'}
                          isArabic={isArabic}
                        />
                        <CustomDropdown
                          name={`to_year_${index}`}
                          value={exp.to_year}
                          onChange={(e) => handleExperienceChange(index, 'to_year', e.target.value)}
                          options={[{ value: '', label: t.year || 'Year' }, ...yearOptions]}
                          placeholder={t.year || 'Year'}
                          isArabic={isArabic}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddExperience}
              style={{
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <FaPlus /> {t.addMore || 'Add More'}
            </button>
          </>
        )}

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onBack({ 
            experience_none: experienceNone, 
            experiences: experienceNone ? [] : experiences 
          })}>
            {t.back || 'Back'}
          </button>
          <button type="submit" className="btn btn-primary">
            {t.saveAndContinue || 'Save and continue'}
          </button>
        </div>
      </form>
    </div>
  );
}